import React, { useCallback, useEffect, useState } from 'react';

import Helmet from 'react-helmet';
import { useSelector } from 'react-redux';

import { Grid, GridColumn } from '@atlaskit/page';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import Sidebar from 'src/app/sidebar';
import { FocusedPageHeader } from 'src/components/accessibility';
import { DiffLoader } from 'src/components/diff-set/diff-loader';
import { DiffProvider } from 'src/components/diff-set/diff-provider';
import { useDiffSetUrls } from 'src/components/diff-set/urls';
import { Commit } from 'src/components/types';
import {
  CommentCreateEvent,
  NewConversationClickEvent,
  CommentExpandCollapseEvent,
} from 'src/contexts/conversations-context';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useIntl } from 'src/hooks/intl';
import { COMMIT_LOAD, MetricSender } from 'src/metrics';
import { getIsUploadImageDialogOpen } from 'src/redux/pull-request/image-upload-reducer';
import {
  getCurrentRepositoryFullSlug,
  getCurrentRepositoryOwnerName,
} from 'src/selectors/repository-selectors';
import {
  publishScreenEvent,
  publishTrackEvent,
  publishUiEvent,
} from 'src/utils/analytics/publish';
import { shortHash, short12Hash } from 'src/utils/short-hash';

import { DiffFeaturesContext } from '../../pull-request/components/diff-features-context';
import { ConnectedImageUploadDialog } from '../../pull-request/containers/image-upload-dialog';
import { useCommitPageReducer } from '../hooks/commit-page-reducer';
import { useFetchCommitAnnotated } from '../utils/use-fetch-annotated';
import { useFetchCommitComments } from '../utils/use-fetch-commit-comments';

import { CommitDetails } from './commit-details';
import { CommitDialogZone } from './commit-dialog-zone';
import { CommitGlobalConversation } from './commit-global-conversation';
import { CommitPageBreadcrumbs } from './commit-page-breadcrumbs';
import messages from './commit-page.i18n';
import * as styles from './commit-page.styled';
import { ConversationContext } from './conversation-context';
import CommitSidebar from './sidebar';
import { UnreachableCommitWarning } from './unreachable-commit-warning';

const diffFeaturesContext = {
  multiCommentReview: false,
  hasMarkFilesAsViewed: false,
  hasAnnotations: false,
  hasComments: true,
  previewMergeOption: false,
  extraStickyHeaderHeight: false,
  hasCommentNav: false,
  hasCodeSuggestions: false,
};

export const CommitPage = ({
  commit: rawCommit,
}: {
  commit: Commit;
}): JSX.Element => {
  const [isDiffLoaded, setIsDiffLoaded] = useState(false);
  const intl = useIntl();

  const { commit, dispatchCommitUpdate } = useCommitPageReducer(rawCommit);
  const { hash } = commit;
  const owner = useSelector(getCurrentRepositoryOwnerName) || '';
  const repoSlug = useSelector(getCurrentRepositoryFullSlug) || '';
  const annotatedRefs = useFetchCommitAnnotated(repoSlug, hash);
  const isImageUploadDialogOpen = useSelector(getIsUploadImageDialogOpen);
  const diffMetadataProps = {
    sourceHash: hash,
    sourceRepoFullSlug: commit.repository.full_name,
  };
  const onDiffLoaded = () => setIsDiffLoaded(true);

  const { diffUrlBuilder, diffStatUrlBuilder } = useDiffSetUrls(repoSlug, hash);
  useFetchCommitComments(owner, repoSlug, commit.hash);

  useEffect(() => {
    publishScreenEvent('commitScreen');
  }, []);

  const onNewConversationClick = useCallback(
    (event: NewConversationClickEvent) => {
      let actionSubjectId = '';
      if (event.lineFrom || event.lineTo) {
        actionSubjectId = 'addDiffCommentButton';
      } else {
        actionSubjectId = 'addFileCommentButton';
      }
      publishUiEvent({
        action: 'clicked',
        actionSubject: 'button',
        actionSubjectId,
        source: 'commitPage',
        attributes: {
          file: event.file,
        },
      });
    },
    []
  );

  const onCommentCreate = useCallback((event: CommentCreateEvent) => {
    publishTrackEvent({
      action: 'created',
      actionSubject: 'commitComment',
      actionSubjectId: event.commentId,
      source: 'commitPage',
      attributes: {
        repoUuid: event.repoUuid,
      },
    });
  }, []);

  const onCommentExpandCollapse = useCallback(
    (event: CommentExpandCollapseEvent) => {
      publishUiEvent({
        action: 'clicked',
        actionSubject: 'collapseExpandButton',
        actionSubjectId: event.action,
        source: 'commitPage',
        attributes: {
          resolved: event.resolved,
          paid: event.paid,
          repoId: event.repoId,
        },
      });
    },
    []
  );

  const eventHandlers = {
    onNewConversationClick,
    onCommentCreate,
    onCommentExpandCollapse,
  };

  const tabTitleChangesEnabled = useStatsigGate(
    StatsigFeatureKeys.tabTitleChangesEnabled
  );

  return (
    <Grid layout="fluid" spacing="comfortable">
      {isImageUploadDialogOpen && <ConnectedImageUploadDialog />}
      <Helmet
        title={
          tabTitleChangesEnabled
            ? `${intl.formatMessage(messages.commit)} ${short12Hash(
                commit.hash
              )}`
            : `/ ${intl.formatMessage(messages.commit)} ${short12Hash(
                commit.hash
              )}`
        }
        defer={false}
      />
      <GridColumn>
        <FocusedPageHeader breadcrumbs={<CommitPageBreadcrumbs />}>
          {shortHash(commit.hash)}
        </FocusedPageHeader>
        {!annotatedRefs.isLoading && annotatedRefs.isUnreachable && (
          <UnreachableCommitWarning />
        )}
        <CommitDetails
          dispatchCommitUpdate={dispatchCommitUpdate}
          commit={commit}
        />
        {commit.rendered &&
        commit.rendered.message &&
        commit.rendered.message.html ? (
          <div
            dangerouslySetInnerHTML={{ __html: commit.rendered.message.html }}
          />
        ) : (
          <styles.CommitDescription>{commit.message}</styles.CommitDescription>
        )}
        <DiffFeaturesContext.Provider value={diffFeaturesContext}>
          <ConversationContext
            commit={commit}
            eventHandlers={eventHandlers}
            supportsCommentResolution={false}
          >
            <CommitGlobalConversation />
            <DiffProvider
              diffUrlBuilder={diffUrlBuilder}
              diffStatUrlBuilder={diffStatUrlBuilder}
              diffMetadataProps={diffMetadataProps}
              singleHashDiff
              maxDiffStatPages={1}
            >
              <DiffLoader onDiffLoaded={onDiffLoaded} />
              {isDiffLoaded && <MetricSender metric={COMMIT_LOAD} />}
              <CommitDialogZone diffMetadataProps={diffMetadataProps} />
            </DiffProvider>
          </ConversationContext>
        </DiffFeaturesContext.Provider>
      </GridColumn>
      <Sidebar>
        <CommitSidebar commit={commit} annotatedRefs={annotatedRefs} />
      </Sidebar>
    </Grid>
  );
};
